/**
 * Block registry entry point (`specs/M6-A3-automation-catalog.md` §5).
 *
 * Adding a block is one file here plus one line in `registerBuiltInBlocks()`; nothing else in
 * the codebase changes and nothing outside a block's own file branches on a block id
 * (CLAUDE.md rule 6).
 *
 * Tier 1 (M6-A1) is the core trigger/condition/action set. Round 6d (M6-A4,
 * `specs/M6-A4-integrations.md`) added the rest of what this account actually uses: the
 * cross-board creators (`15499`, `5826`, `6899`, `496691`), the board-template block (`88520`),
 * the Slack (`7459`) / Gmail / Google Calendar actions, the generic outgoing webhook and the
 * triggers the twelve webhook recipes need — including the five **subitem** ones, which are
 * `scope: 'subitems'` because their events fire on the subitems board while the recipe lives on
 * the parent board.
 *
 * Still deliberately absent, and therefore still importing `partial`/`unmapped` (R7): the two
 * monday-AI blocks (`947283`, `947304`) and the Emails & Activities triggers (`496714`,
 * `496716`, `496693`) with their two notify actions (`496697`, `496741`).
 */
import { automationBlockRegistry, isActionBlock, isConditionBlock, isTriggerBlock } from '../block.js';
import { statusChangesToTrigger } from './223025.js';
import { statusChangesFromToTrigger } from './223089.js';
import { statusChangesTrigger } from './53.js';
import { columnChangesTrigger } from './54.js';
import { itemCreatedTrigger } from './55.js';
import { buttonClickedTrigger } from './963.js';
import { dateArrivesTrigger } from './580.js';
import { everyTimePeriodTrigger } from './47.js';
import { cronOneOffTrigger } from './ez.t.cron-oneoff.js';
import { itemMovedToGroupTrigger } from './ez.t.item-moved-to-group.js';
import { itemCreatedInGroupTrigger } from './ez.t.item-created-in-group.js';
import { itemNameChangedTrigger } from './ez.t.item-name-changed.js';
import { itemDeletedTrigger } from './ez.t.item-deleted.js';
import { updateCreatedTrigger } from './ez.t.update-created.js';
import { updateChangedTrigger } from './ez.t.update-changed.js';
import { updateDeletedTrigger } from './ez.t.update-deleted.js';
import { itemCreatedOrUpdatedTrigger } from './ez.t.item-created-or-updated.js';
import { SUBITEM_TRIGGERS } from './ez.t.subitems.js';
import { statusIsCondition } from './1838.js';
import { dropdownContainsCondition } from './78324.js';
import { personAssignedCondition } from './1810.js';
import { toValueCondition } from './45.js';
import { fromToCondition } from './48.js';
import { moveToGroupAction } from './40.js';
import { setStatusAction } from './41.js';
import { setDateTodayAction } from './380.js';
import { pushDateAction } from './468.js';
import { assignPersonAction } from './50.js';
import { assignCreatorAction } from './1822.js';
import { createItemAction } from './507.js';
import { createSubitemAction } from './ez.a.create-subitem.js';
import { notifyAction, notifyActionLegacy51, notifyActionLegacy5100 } from './5200.js';
import { createUpdateAction } from './5941.js';
import { archiveItemAction } from './42429.js';
import { deleteItemAction } from './42432.js';
import { duplicateGroupAction } from './1825.js';
import { clearColumnAction, setColumnAction } from './ez.a.set-column.js';
import { premadeUpdateAction } from './ez.a.create-premade-update.js';
import { createItemInBoardAction } from './5826.js';
import { createItemAndConnectAction } from './15499.js';
import { moveItemToBoardAction } from './6899.js';
import { activityCreateItemAction } from './496691.js';
import { createBoardFromTemplateAction } from './88520.js';
import { slackNotifyChannelAction, slackNotifyUserAction, slackSendUpdateAction } from './7459.js';
import { sendEmailAction } from './ez.a.send-email.js';
import { createCalendarEventAction } from './ez.a.create-calendar-event.js';
import { sendWebhookAction } from './ez.a.send-webhook.js';
export * from './223025.js';
export * from './223089.js';
export * from './53.js';
export * from './54.js';
export * from './55.js';
export * from './963.js';
export * from './580.js';
export * from './47.js';
export * from './ez.t.cron-oneoff.js';
export * from './ez.t.item-moved-to-group.js';
export * from './ez.t.item-created-in-group.js';
export * from './ez.t.item-name-changed.js';
export * from './ez.t.item-deleted.js';
export * from './ez.t.update-created.js';
export * from './ez.t.update-changed.js';
export * from './ez.t.update-deleted.js';
export * from './ez.t.item-created-or-updated.js';
export * from './ez.t.subitems.js';
export * from './1838.js';
export * from './78324.js';
export * from './1810.js';
export * from './45.js';
export * from './48.js';
export * from './40.js';
export * from './41.js';
export * from './380.js';
export * from './468.js';
export * from './50.js';
export * from './1822.js';
export * from './507.js';
export * from './ez.a.create-subitem.js';
export * from './5200.js';
export * from './5941.js';
export * from './42429.js';
export * from './42432.js';
export * from './1825.js';
export * from './ez.a.set-column.js';
export * from './ez.a.create-premade-update.js';
export * from './5826.js';
export * from './15499.js';
export * from './6899.js';
export * from './496691.js';
export * from './88520.js';
export * from './7459.js';
export * from './ez.a.send-email.js';
export * from './ez.a.create-calendar-event.js';
export * from './ez.a.send-webhook.js';
export * from './email-receiver.js';
export * from './receiver.js';
export * from './token-context.js';
const BUILT_IN = [
    // triggers, most-used first (`specs/M6-A3` §10)
    statusChangesToTrigger,
    everyTimePeriodTrigger,
    statusChangesTrigger,
    itemCreatedTrigger,
    dateArrivesTrigger,
    buttonClickedTrigger,
    columnChangesTrigger,
    statusChangesFromToTrigger,
    itemMovedToGroupTrigger,
    itemNameChangedTrigger,
    itemDeletedTrigger,
    itemCreatedInGroupTrigger,
    updateCreatedTrigger,
    updateChangedTrigger,
    updateDeletedTrigger,
    itemCreatedOrUpdatedTrigger,
    ...SUBITEM_TRIGGERS,
    cronOneOffTrigger,
    // conditions (§11)
    toValueCondition,
    statusIsCondition,
    dropdownContainsCondition,
    fromToCondition,
    personAssignedCondition,
    // actions (§12)
    moveToGroupAction,
    createItemAction,
    setStatusAction,
    notifyAction,
    setDateTodayAction,
    assignCreatorAction,
    assignPersonAction,
    createUpdateAction,
    pushDateAction,
    notifyActionLegacy51,
    notifyActionLegacy5100,
    archiveItemAction,
    deleteItemAction,
    duplicateGroupAction,
    setColumnAction,
    clearColumnAction,
    premadeUpdateAction,
    createSubitemAction,
    // M6-A4 (`specs/M6-A4-integrations.md`)
    createItemAndConnectAction,
    createItemInBoardAction,
    moveItemToBoardAction,
    createBoardFromTemplateAction,
    activityCreateItemAction,
    slackNotifyChannelAction,
    slackNotifyUserAction,
    slackSendUpdateAction,
    sendEmailAction,
    createCalendarEventAction,
    sendWebhookAction,
];
let registered = false;
export function registerBuiltInBlocks() {
    if (registered)
        return;
    automationBlockRegistry.registerAll(BUILT_IN);
    registered = true;
}
registerBuiltInBlocks();
export const triggerBlocks = () => automationBlockRegistry.list().filter(isTriggerBlock);
export const conditionBlocks = () => automationBlockRegistry.list().filter(isConditionBlock);
export const actionBlocks = () => automationBlockRegistry.list().filter(isActionBlock);
/**
 * **Is this block offerable on a board with these column types?** — one predicate, next to the
 * registry, for the builder's palette *and* the api's `automations.catalog` (P-201).
 *
 * Two terms, and the api used to apply only the second:
 *
 *  - an action registered `executable: false` exists so its imported automations keep their
 *    1:1 shape (R7); nothing implements it, so nothing may offer it. Triggers and conditions
 *    carry no such flag and are never withheld by this term.
 *  - `requiredColumnTypes` is monday's own rule that a recipe the board has no column for is
 *    not shown at all (M1-B1 §3.2).
 *
 * The two disagreed: `RecipeEditor#availableBlocks` filtered on both, `catalogForBoard` on the
 * second alone, so the endpoint an API consumer would build a palette from called `496691`
 * available while the builder withheld it. Both now call this.
 */
export function isBlockAvailable(block, columnTypes) {
    if (isActionBlock(block) && !block.executable)
        return false;
    return block.requiredColumnTypes === undefined || block.requiredColumnTypes.some((type) => columnTypes.includes(type));
}
/** Every trigger that subscribes to an outbox event type — the matching index's key space. */
export function triggersForEventType(type) {
    return triggerBlocks().filter((block) => block.subscribes.includes(type));
}
//# sourceMappingURL=index.js.map